/**
 * Route-level code splitting. Every page (and the operator-only platform area)
 * loads as its own chunk the first time it is visited, so the entry bundle holds
 * only the sign-in screen, the tenant shell and the libraries every page shares.
 *
 * Each page keeps a `preload` that starts the same import; the navigation calls
 * `preloadPath` on hover or focus so the chunk is usually in flight before the
 * click. A failed import is not cached here, but React.lazy keeps the rejection,
 * so recovery is a page reload (offered by RouteBoundary).
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

/**
 * React.lazy is bounded by ComponentType<any>: a page's props are only known per
 * page, and `never` or `unknown` props would reject every page that takes some.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- React.lazy's own bound
type PageComponent = ComponentType<any>;

type Preloadable<C extends PageComponent> = LazyExoticComponent<C> & {
  readonly preload: () => Promise<unknown>;
};

/** React.lazy for a named export, plus a `preload` sharing the same import. */
function lazyPage<M extends Record<K, PageComponent>, K extends keyof M & string>(
  load: () => Promise<M>,
  name: K,
): Preloadable<M[K]> {
  let pending: Promise<M> | null = null;
  const start = (): Promise<M> => {
    pending ??= load().catch((error: unknown) => {
      pending = null;
      throw error;
    });
    return pending;
  };
  const component = lazy(async () => ({ default: (await start())[name] }));
  return Object.assign(component, { preload: start });
}

// --- tenant workspace pages --------------------------------------------------
export const DashboardPage = lazyPage(() => import('../pages/DashboardPage'), 'DashboardPage');
export const OpinionMonitoringPage = lazyPage(
  () => import('../pages/OpinionMonitoringPage'),
  'OpinionMonitoringPage',
);
export const ReportCenterPage = lazyPage(
  () => import('../pages/ReportCenterPage'),
  'ReportCenterPage',
);
export const ReportDetailPage = lazyPage(
  () => import('../pages/ReportDetailPage'),
  'ReportDetailPage',
);
export const IntelPage = lazyPage(() => import('../pages/IntelPage'), 'IntelPage');
export const MultimodalDemoPage = lazyPage(
  () => import('../pages/MultimodalDemoPage'),
  'MultimodalDemoPage',
);
export const PropagationAnalysisPage = lazyPage(
  () => import('../pages/PropagationAnalysisPage'),
  'PropagationAnalysisPage',
);
export const AlertsPage = lazyPage(() => import('../pages/AlertsPage'), 'AlertsPage');
export const AlertDetailPage = lazyPage(
  () => import('../pages/AlertDetailPage'),
  'AlertDetailPage',
);
export const TasksPage = lazyPage(() => import('../pages/TasksPage'), 'TasksPage');
export const TaskDetailPage = lazyPage(() => import('../pages/TaskDetailPage'), 'TaskDetailPage');
export const TopicsPage = lazyPage(() => import('../pages/TopicsPage'), 'TopicsPage');
export const TopicDetailPage = lazyPage(
  () => import('../pages/TopicDetailPage'),
  'TopicDetailPage',
);
export const CrawlersPage = lazyPage(() => import('../pages/CrawlersPage'), 'CrawlersPage');
export const ContentsPage = lazyPage(() => import('../pages/ContentsPage'), 'ContentsPage');
export const ContentRevisionPage = lazyPage(
  () => import('../pages/ContentRevisionPage'),
  'ContentRevisionPage',
);
export const DeletionAssessmentPage = lazyPage(
  () => import('../pages/DeletionAssessmentPage'),
  'DeletionAssessmentPage',
);
export const JobsPage = lazyPage(() => import('../pages/JobsPage'), 'JobsPage');
export const BillingPage = lazyPage(() => import('../features/billing/BillingPage'), 'BillingPage');
export const SupportAccessPage = lazyPage(
  () => import('../features/support/SupportAccessPage'),
  'SupportAccessPage',
);
export const PasswordPage = lazyPage(() => import('../pages/PasswordPage'), 'PasswordPage');

// --- platform area (operators only) -------------------------------------------
const platformOverview = () => import('../features/platform/PlatformOverview');
const platformUsers = () => import('../features/platform/PlatformUsers');
const platformTenants = () => import('../features/platform/PlatformTenants');
const platformSources = () => import('../features/platform/PlatformSources');
const platformEvaluation = () => import('../features/platform/PlatformEvaluation');

export const PlatformLayout = lazyPage(
  () => import('../features/platform/PlatformLayout'),
  'PlatformLayout',
);
export const PlatformWorkspace = lazyPage(
  () => import('../features/platform/PlatformWorkspace'),
  'PlatformWorkspace',
);
export const PlatformOverview = lazyPage(platformOverview, 'PlatformOverview');
export const PlatformRuntime = lazyPage(platformOverview, 'PlatformRuntime');
export const PlatformAudit = lazyPage(platformOverview, 'PlatformAudit');
export const PlatformUsers = lazyPage(platformUsers, 'PlatformUsers');
export const PlatformUserDetail = lazyPage(platformUsers, 'PlatformUserDetail');
export const PlatformTenants = lazyPage(platformTenants, 'PlatformTenants');
export const PlatformTenantDetail = lazyPage(platformTenants, 'PlatformTenantDetail');
export const PlatformSources = lazyPage(platformSources, 'PlatformSources');
export const PlatformPrivateSources = lazyPage(platformSources, 'PlatformPrivateSources');
export const PlatformWebsiteRules = lazyPage(
  () => import('../features/platform/PlatformWebsiteRules'),
  'PlatformWebsiteRules',
);
export const PlatformEvaluation = lazyPage(platformEvaluation, 'PlatformEvaluation');
export const PlatformEvaluationSet = lazyPage(platformEvaluation, 'PlatformEvaluationSet');
export const PlatformBilling = lazyPage(
  () => import('../features/platform/PlatformBilling'),
  'PlatformBilling',
);

/**
 * Navigation targets → the chunk they open. Paths are workspace-relative for the
 * tenant nav (the platform workspace passes the same unprefixed path).
 */
const PRELOAD_BY_PATH: Readonly<Record<string, () => Promise<unknown>>> = {
  '/': DashboardPage.preload,
  '/analysis/monitoring': OpinionMonitoringPage.preload,
  '/analysis/intel': IntelPage.preload,
  '/analysis/multimodal': MultimodalDemoPage.preload,
  '/reports': ReportCenterPage.preload,
  '/alerts': AlertsPage.preload,
  '/tasks': TasksPage.preload,
  '/topics': TopicsPage.preload,
  '/crawlers': CrawlersPage.preload,
  '/contents': ContentsPage.preload,
  '/jobs': JobsPage.preload,
  '/billing': BillingPage.preload,
  '/support': SupportAccessPage.preload,
  '/private-sources': PlatformPrivateSources.preload,
  '/platform': PlatformOverview.preload,
  '/platform/tenants': PlatformTenants.preload,
  '/platform/users': PlatformUsers.preload,
  '/platform/sources': PlatformSources.preload,
  '/platform/website-rules': PlatformWebsiteRules.preload,
  '/platform/evaluation': PlatformEvaluation.preload,
  '/platform/runtime': PlatformRuntime.preload,
  '/platform/audit': PlatformAudit.preload,
};

/** Starts loading the chunk behind a nav link; failures surface on the real visit. */
export function preloadPath(path: string): void {
  const preload = PRELOAD_BY_PATH[path];
  if (preload !== undefined) void preload().catch(() => undefined);
}
